import {AfterViewInit, Component, ElementRef, OnDestroy, TemplateRef, ViewChild, ViewContainerRef, ChangeDetectionStrategy} from '@angular/core';
import {EventGateway} from '../event-gateway';
import {openConfirmationModalWithCallback} from "../utils";
import {ModalConfirmAutofocus, ModalConfirmAutofocusData} from "../modal/modal-confirm.component";
import lodash from "lodash";

@Component({
    selector: 'app-edit-modal',
    templateUrl: './edit-modal.component.html',
    styleUrls: ['./edit-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditModalComponent implements AfterViewInit, OnDestroy {

  @ViewChild('modal', {static: true}) modal: ElementRef;
  @ViewChild('container', {read: ViewContainerRef}) container: ViewContainerRef;
  @ViewChild('stackContainer', {read: ViewContainerRef}) stackContainer: ViewContainerRef;
  private readonly registration: () => void;

  modalOptions: EditModalOptions;
  onHideConfirmation: ConfirmationInfo = {};
  queuedEvent: EditModalEvent;
  // Tracks the ACTUAL Bootstrap modal visibility (set on `shown.bs.modal`/`hidden.bs.modal`),
  // as opposed to mere DOM/component presence in `container`. Bootstrap's own `hide()` no-ops
  // when the modal isn't shown or is mid-transition, so a reopen guard keyed off DOM presence
  // alone can queue a reopen behind a close that will never fire `hidden.bs.modal` again,
  // stranding `queuedEvent` forever. Keying the guard off this flag instead lets a new open
  // proceed directly once the modal is genuinely no longer shown.
  private isShown = false;

  constructor(private eventGateway: EventGateway) {
    this.registration = this.eventGateway.registerLocalHandler(this);
  }

  ngAfterViewInit(): void {
    $(this.modal.nativeElement).on('shown.bs.modal', () => this.isShown = true);
    $(this.modal.nativeElement).on('hidden.bs.modal', this.editModalHiddenCallback);
    $(this.modal.nativeElement).on('hide.bs.modal', this.confirmOnHideCallback);
  }

  'openEditModal' = (event: EditModalEvent) => {
    if (this.isShown) {
      if (event.options?.currentToStack) {
        lodash.range(0, this.container.length).forEach(i =>
          this.stackContainer.insert(this.container.get(i), this.stackContainer.length));
      } else {
        this.queuedEvent = event;
        this.closeEditModal();
        return;
      }
    }
    if (event.component instanceof TemplateRef) {
      this.container.createEmbeddedView(event.component);
    } else {
      const dialogRef = this.container.createComponent(<any>event.component);
      dialogRef.instance['data'] = event.data;
      $(dialogRef.location.nativeElement).addClass('modal-content');
    }
    this.onHideConfirmation = event.options?.currentToStack ? this.onHideConfirmation || {} : {};
    this.modalOptions = event.options || {};
    this.applyStyling(this.modalOptions);
    $(this.modal.nativeElement).modal('show');
    this.queuedEvent = null;
  }

  'closeEditModal' = () => {
    $(this.modal.nativeElement).modal('hide');
  }

  'confirmationModalOpened' = () => {
    if (this.onHideConfirmation && this.modalOptions?.currentToStack && !this.onHideConfirmation.modalOpened) {
      this.onHideConfirmation.modalOpened = true;
      this.closeEditModal();
    }
  }

  'confirmationModalClosed' = (confirmed: boolean) => {
    if (this.onHideConfirmation?.modalOpened) {
      $(this.modal.nativeElement).modal('show');
      this.onHideConfirmation.modalOpened = false;
    }
  }

  ngOnDestroy(): void {
    $(this.modal.nativeElement).modal('hide');
    this.registration();
  }

  private applyStyling = (modalOptions: EditModalOptions) => {
    const dialog = $(this.modal.nativeElement);
    if (modalOptions?.leftToRight) {
      dialog.addClass("left-to-right");
    } else {
      dialog.removeClass("left-to-right");
    }
    dialog.removeClass('dialog-sm').removeClass('dialog-md').removeClass('dialog-auto');
    if (modalOptions?.size) {
      dialog.addClass(`dialog-${modalOptions.size}`);
    }
  }

  toggleOnChange = () => {
    this.onHideConfirmation.changed = true;
  }

  'formDataSaved' = () => {
    this.onHideConfirmation.changed = false;
  }

  'showPreviousInStack' = (options: EditModalStackOptions) => {
    lodash.range(0, Math.min(this.stackContainer.length + 1, options.amount || 1))
      .forEach(i => {
        if (this.stackContainer.length === 0) {
          if (options.endReached) options.endReached();
          return;
        }
        this.container.clear();
        this.container.insert(this.stackContainer.get(this.stackContainer.length - 1), 0);
      });
  }

  private confirmOnHideCallback = (e) => {
    if (this.modalOptions?.warnOnClose && this.onHideConfirmation?.changed && !this.onHideConfirmation.approved && !this.onHideConfirmation.modalOpened) {
      openConfirmationModalWithCallback((confirmed) => {
        this.onHideConfirmation.modalOpened = false;
        if (confirmed) {
          this.onHideConfirmation.approved = true;
          this.queuedEvent = null;
          $(this.modal.nativeElement).modal('hide');
          // The Bootstrap hide triggered above is a no-op by the time the user answers this dialog -
          // the real hide already ran reentrantly from this method's own closeEditModal() call below,
          // BEFORE the confirm dialog was even shown (Bootstrap's Modal.hide() guards
          // `if (!this._isShown || this._isTransitioning) return`, so calling hide() again here fires
          // no further 'hidden.bs.modal' event). editModalHiddenCallback - the ONLY other place that
          // clears the containers and drains queuedEvent - already ran ONCE for this cycle, while
          // modalOpened was still true, which deliberately skipped clearing so the still-being-decided
          // content wasn't destroyed mid-dialog. Without this explicit call, the container is left
          // non-empty forever after "Discard changes", so the NEXT openEditModal() sees
          // container.length > 0 and silently queues-and-defers behind a closeEditModal() that is ALSO
          // a no-op - the queued open is then stranded forever, with zero errors. Finalize the close
          // directly here instead of waiting for a 'hidden.bs.modal' event that will not come.
          this.clearContainers();
        } else {
          $(this.modal.nativeElement).modal('show');
        }
      }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
        title: "You have unsaved changes",
        message: "Your changes have not been saved. Are you sure you want to discard your changes?",
        confirmText: "Discard changes",
        cancelText: "Keep editing"
      }, "static");
      this.onHideConfirmation.modalOpened = true;
      this.closeEditModal();
      e.preventDefault();
      return false;
    }
    return true;
  };

  private editModalHiddenCallback = () => {
    if (!this.onHideConfirmation.modalOpened) {
      this.clearContainers();
    }
    if (this.queuedEvent) {
      this.openEditModal(this.queuedEvent);
    }
  }

  // Shared by editModalHiddenCallback (the normal close path, once Bootstrap's own 'hidden.bs.modal'
  // fires) and confirmOnHideCallback's "Discard changes" branch (which has no such event left to hang
  // cleanup off, since the real hide already completed earlier - see the comment there). Clears BOTH
  // containers (the whole stack, not just the top) because this only ever runs on a genuine final
  // close of the entire modal - stack push/pop (currentToStack) never touches Bootstrap's show/hide at
  // all (openEditModal's own modal('show') call is a no-op when the modal is already shown), so a real
  // 'hidden.bs.modal'/finalized-discard always means the whole stack is going away. Resetting
  // onHideConfirmation here too prevents a stale approved/changed flag from an earlier
  // discard-and-close from leaking into a LATER, unrelated modal session that happens to reuse the
  // same object reference via currentToStack's "preserve across stack pushes" ternary in openEditModal.
  private clearContainers = () => {
    this.isShown = false;
    this.container.clear();
    this.stackContainer.clear();
    this.onHideConfirmation = {};
  }
}

interface ConfirmationInfo {
  changed?: boolean;
  modalOpened?: boolean;
  approved?: boolean;
}

export interface EditModalEvent {
  component;
  data?: any;
  options?: EditModalOptions;
}

export interface EditModalOptions {
  leftToRight?: boolean;
  size?: 'sm' | 'md' | 'auto';
  warnOnClose?: boolean;
  currentToStack?: boolean;
}

export interface EditModalStackOptions {
  amount?: number;
  endReached?: () => void;
}
